import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import { Input, Select, TextArea } from '../common/FormFields';
import { queueEmail } from '../../services/emailService';

export const EnterpriseInquiry = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    employeeCount: '',
    locations: '',
    modules: {
      roadscope: false,
      roadguard: false,
      cargocare: false,
      permitpro: false
    },
    licenseCount: '',
    additionalInfo: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Send inquiry email to admin
      await queueEmail('admin@oversizehub.net', 'New Enterprise Inquiry', {
        template: 'ENTERPRISE_INQUIRY',
        variables: {
          companyName: formData.companyName,
          employeeCount: formData.employeeCount,
          locations: formData.locations,
          modules: Object.entries(formData.modules)
            .filter(([_, selected]) => selected)
            .map(([name]) => name)
            .join(', '),
          licenseCount: formData.licenseCount,
          additionalInfo: formData.additionalInfo
        }
      });

      // Send confirmation to user
      const userEmail = localStorage.getItem('pendingEmail');
      if (userEmail) {
        await queueEmail(userEmail, 'Enterprise Inquiry Received', {
          template: 'ENTERPRISE_INQUIRY_CONFIRMATION',
          variables: {
            companyName: formData.companyName
          }
        });
      }

      navigate('/enterprise-inquiry-submitted');
    } catch (err) {
      setError('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-[#2A2B2E] rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Enterprise Account Information</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
                {error}
              </div>
            )}

            <Input
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Select
              label="Number of Employees"
              value={formData.employeeCount}
              onChange={(value) => setFormData({ ...formData, employeeCount: value })}
              options={[
                { value: '1-10', label: '1-10 employees' },
                { value: '11-50', label: '11-50 employees' },
                { value: '51-200', label: '51-200 employees' },
                { value: '201-500', label: '201-500 employees' },
                { value: '500+', label: 'More than 500 employees' }
              ]}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Number of Locations"
              value={formData.locations}
              onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Required Modules
              </label>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries({
                  roadscope: 'RoadScope',
                  roadguard: 'RoadGuard',
                  cargocare: 'CargoCare',
                  permitpro: 'PermitPro'
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.modules[key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        modules: {
                          ...formData.modules,
                          [key]: e.target.checked
                        }
                      })}
                      className="rounded border-gray-600 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-white">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Input
              label="Required Number of Licenses"
              value={formData.licenseCount}
              onChange={(e) => setFormData({ ...formData, licenseCount: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <TextArea
              label="Additional Information"
              value={formData.additionalInfo}
              onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
              placeholder="Please provide any additional information that would help us understand your needs better"
              rows={4}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => navigate('/plans')}
                variant="default"
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
              >
                Back
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Submit Inquiry
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};